import React from "react";
import { format } from "date-fns";

export const CLAIM_LIFECYCLE_STATUSES = [
  "Submitted",
  "Cargo Release Signed (Payment Pending)",
  "Partial Payment Received",
  "Claim Closed – Paid in Full",
  "Claim Closed – Claim Resolved",
  "Claim Closed – Claim Rejected",
];

export const CLAIM_STATUSES = ["Draft", ...CLAIM_LIFECYCLE_STATUSES];

export const CLAIM_TYPES = [
  "Damage",
  "Shortage",
  "Loss",
  "Delay",
  "Service Failure",
];

export const CLOSED_CLAIM_STATUSES = new Set([
  "Claim Closed – Paid in Full",
  "Claim Closed – Claim Resolved",
  "Claim Closed – Claim Rejected",
]);

export const claimStatusStyles = {
  Draft: "bg-orange-200 dark:bg-orange-700/60 text-gray-800 dark:text-gray-200",
  Submitted: "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  "Cargo Release Signed (Payment Pending)":
    "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  "Partial Payment Received":
    "bg-teal-100 dark:bg-teal-900/40 text-black dark:text-teal-200",
  "Claim Closed – Paid in Full":
    "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  "Claim Closed – Claim Resolved":
    "bg-neutral-200 dark:bg-neutral-700/40 text-gray-800 dark:text-gray-200",
  "Claim Closed – Claim Rejected":
    "bg-red-300 dark:bg-red-900/40 text-black dark:text-red-200",
};

export const claimTypeStyles = {
  Damage: "bg-red-100 dark:bg-red-900/40 text-black dark:text-red-200",
  Shortage: "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  Loss: "bg-purple-100 dark:bg-purple-900/40 text-black dark:text-purple-200",
  Delay: "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  "Service Failure":
    "bg-neutral-200 dark:bg-neutral-700/40 text-gray-800 dark:text-gray-200",
};

export const formatClaimMoney = (value) => {
  if (value == null || value === "") {
    return (
      <span className="font-medium text-gray-400 dark:text-acs-dark-subtext">—</span>
    );
  }
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return (
    <span className="font-semibold tabular-nums text-acs-light-text dark:text-acs-dark-text">
      ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

export const formatClaimDate = (value) => {
  if (!value) return "—";
  try {
    return format(new Date(value), "MM/dd/yyyy");
  } catch {
    return value;
  }
};

export const ClaimStatusBadge = ({ status, uppercase = true }) => {
  if (!status) {
    return (
      <span className="text-gray-400 dark:text-acs-dark-subtext font-medium">—</span>
    );
  }
  const styles =
    claimStatusStyles[status] ||
    "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200";
  const label = uppercase ? status.toUpperCase() : status;
  return (
    <span
      className={`inline-block px-fluid-1.5 py-fluid-1 rounded font-medium text-fluid-xs whitespace-nowrap ${styles}`}
    >
      {label}
    </span>
  );
};

export const ClaimTypeBadge = ({ type }) => {
  if (!type) return "—";
  const styles =
    claimTypeStyles[type] ||
    "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200";
  return (
    <span
      className={`inline-block px-fluid-1.5 py-fluid-1 rounded font-medium text-fluid-xs whitespace-nowrap ${styles}`}
    >
      {type}
    </span>
  );
};

export const MOCK_CLAIMS = [
  {
    id: "CLM-2026-0041",
    claimNumber: "CLM-2026-0041",
    shipmentNumber: "DIAMOND-LAT-AA00190",
    claimType: "Damage",
    status: "Cargo Release Signed (Payment Pending)",
    dateFiled: "2026-06-24",
    dateIssueOccurred: "2026-06-22",
    totalClaimAmount: 8420,
    amountApproved: 8420,
    amountPaid: 0,
    additionalCosts: 0,
    description: "4 pallets crushed in transit, received 06/22.",
    relatedStop: "Stop 2 — Delivery (COSTCO #1019 CNW)",
    poNumber: "SP2026051502",
    carrierName: "SCHNEIDER NATIONAL",
    carrierOfficeCode: "DIAMOND-META",
    shipmentOfficeCode: "DIAMOND-META",
  },
  {
    id: "CLM-2026-0040",
    claimNumber: "CLM-2026-0040",
    shipmentNumber: "DIAMOND-LAT-AA00188",
    claimType: "Shortage",
    status: "Submitted",
    dateFiled: "2026-06-20",
    dateIssueOccurred: "2026-06-18",
    totalClaimAmount: 2150,
    amountApproved: null,
    amountPaid: 0,
    additionalCosts: 0,
    description: "",
    relatedStop: null,
    poNumber: "SP2026051212",
    carrierName: "—",
    carrierOfficeCode: "DIAMOND-META",
    shipmentOfficeCode: "DIAMOND-META",
  },
  {
    id: "CLM-2026-0038",
    claimNumber: "CLM-2026-0038",
    shipmentNumber: "DIAMOND-RUS00000016",
    claimType: "Delay",
    status: "Partial Payment Received",
    dateFiled: "2026-06-11",
    dateIssueOccurred: "2026-06-09",
    totalClaimAmount: 6300,
    amountApproved: 5000,
    amountPaid: 2500,
    additionalCosts: 200,
    description: "",
    relatedStop: null,
    poNumber: "0119406182",
    carrierName: "RBTW LOGISTICS",
    carrierOfficeCode: "RBTW",
    shipmentOfficeCode: "DIAMOND-META",
  },
  {
    id: "CLM-2026-0035",
    claimNumber: "CLM-2026-0035",
    shipmentNumber: "DIAMOND-LAT-AA00181",
    claimType: "Loss",
    status: "Claim Closed – Paid in Full",
    dateFiled: "2026-05-29",
    dateIssueOccurred: "2026-05-27",
    totalClaimAmount: 14200,
    amountApproved: 14200,
    amountPaid: 14200,
    additionalCosts: 0,
    description: "",
    relatedStop: null,
    poNumber: "SP2026051207",
    carrierName: "—",
    carrierOfficeCode: "DIAMOND-META",
    shipmentOfficeCode: "DIAMOND-META",
  },
  {
    id: "CLM-2026-0033",
    claimNumber: "CLM-2026-0033",
    shipmentNumber: "DIAMOND-LAT-AA00178",
    claimType: "Service Failure",
    status: "Claim Closed – Claim Resolved",
    dateFiled: "2026-05-18",
    dateIssueOccurred: "2026-05-16",
    totalClaimAmount: 900,
    amountApproved: 900,
    amountPaid: 900,
    additionalCosts: 0,
    description: "",
    relatedStop: null,
    poNumber: "SP2026051201",
    carrierName: "J.B. HUNT",
    carrierOfficeCode: "JBHT",
    shipmentOfficeCode: "DIAMOND-META",
  },
  {
    id: "CLM-2026-0031",
    claimNumber: "CLM-2026-0031",
    shipmentNumber: "DIAMOND-LAT-AA00175",
    claimType: "Damage",
    status: "Claim Closed – Claim Rejected",
    dateFiled: "2026-05-09",
    dateIssueOccurred: "2026-05-07",
    totalClaimAmount: 3750,
    amountApproved: 0,
    amountPaid: 0,
    additionalCosts: 0,
    description: "",
    relatedStop: null,
    poNumber: "SP2026051200",
    carrierName: "—",
    carrierOfficeCode: "DIAMOND-META",
    shipmentOfficeCode: "DIAMOND-META",
  },
  {
    id: "CLM-2026-0044",
    claimNumber: "CLM-2026-0044",
    shipmentNumber: "DIAMOND-GAS00113",
    claimType: "Damage",
    status: "Cargo Release Signed (Payment Pending)",
    dateFiled: "2026-06-28",
    dateIssueOccurred: "2026-06-26",
    totalClaimAmount: 1890,
    amountApproved: 1890,
    amountPaid: null,
    additionalCosts: 0,
    description: "Product damage at delivery.",
    relatedStop: "Stop 2 — Delivery (COSTCO #1019 CNW)",
    poNumber: "PO_GOPIMAY20",
    carrierName: "C. H. ROBINSON COMPANY",
    carrierOfficeCode: "RBTW",
    shipmentOfficeCode: "DIAMOND-META",
  },
];

/** Shipment lookup for standalone Claims module — drives Related Shipment select + derived fields */
export const SHIPMENT_CLAIM_OPTIONS = [
  {
    shipmentNumber: "DIAMOND-LAT-AA00190",
    poNumber: "SP2026051502",
    origin: "Diamond Pet Foods - Lathrop",
    destination: "COSTCO #1019 CNW",
    carrierName: "SCHNEIDER NATIONAL",
    stops: [
      { value: "Stop 1 — Pickup (Diamond Pet Foods - Lathrop)", label: "Stop 1 — Pickup (Diamond Pet Foods - Lathrop)" },
      { value: "Stop 2 — Delivery (COSTCO #1019 CNW)", label: "Stop 2 — Delivery (COSTCO #1019 CNW)" },
    ],
  },
  {
    shipmentNumber: "DIAMOND-LAT-AA00188",
    poNumber: "SP2026051212",
    origin: "Diamond Pet Foods - Lathrop",
    destination: "COSTCO #1020 CNW",
    carrierName: "—",
    stops: [
      { value: "Stop 1 — Pickup (Diamond Pet Foods - Lathrop)", label: "Stop 1 — Pickup (Diamond Pet Foods - Lathrop)" },
      { value: "Stop 2 — Delivery (COSTCO #1020 CNW)", label: "Stop 2 — Delivery (COSTCO #1020 CNW)" },
    ],
  },
  {
    shipmentNumber: "DIAMOND-RUS00000016",
    poNumber: "0119406182",
    origin: "Diamond Pet Foods - Russellville",
    destination: "Walmart DC #6042",
    carrierName: "RBTW LOGISTICS",
    stops: [
      { value: "Stop 1 — Pickup (Diamond Pet Foods - Russellville)", label: "Stop 1 — Pickup (Diamond Pet Foods - Russellville)" },
      { value: "Stop 2 — Delivery (Walmart DC #6042)", label: "Stop 2 — Delivery (Walmart DC #6042)" },
    ],
  },
  {
    shipmentNumber: "DIAMOND-GAS00113",
    poNumber: "PO_GOPIMAY20",
    origin: "Diamond Pet Foods - Gaston",
    destination: "COSTCO #1019 CNW",
    carrierName: "C. H. ROBINSON COMPANY",
    stops: [
      { value: "Stop 1 — Pickup (Diamond Pet Foods - Gaston)", label: "Stop 1 — Pickup (Diamond Pet Foods - Gaston)" },
      { value: "Stop 2 — Delivery (COSTCO #1019 CNW)", label: "Stop 2 — Delivery (COSTCO #1019 CNW)" },
    ],
  },
];

export const getShipmentClaimContext = (shipmentNumber) =>
  SHIPMENT_CLAIM_OPTIONS.find((s) => s.shipmentNumber === shipmentNumber) || null;

let submittedClaims = [];

export const addSubmittedClaim = (claim) => {
  submittedClaims = [...submittedClaims, claim];
  return claim;
};

export const generateClaimNumber = () => {
  const year = new Date().getFullYear();
  const seq = String(MOCK_CLAIMS.length + submittedClaims.length + 1).padStart(4, "0");
  return `CLM-${year}-${seq}`;
};

export const getAllClaims = (access) => {
  const all = [...MOCK_CLAIMS, ...submittedClaims];

  if (!access?.isCarrier) {
    return all;
  }

  const carrierCode = (access.officeCode || "").trim().toUpperCase();
  if (!carrierCode) {
    return [];
  }

  return all.filter((claim) => {
    const claimCarrierCode = (claim.carrierOfficeCode || "").trim().toUpperCase();
    return claimCarrierCode === carrierCode;
  });
};

export const getClaimsForShipment = (shipmentNumber) => {
  const base = MOCK_CLAIMS.filter((c) => c.shipmentNumber === shipmentNumber);
  const submitted = submittedClaims.filter((c) => c.shipmentNumber === shipmentNumber);
  return [...base, ...submitted];
};

export const getClaimsCount = (shipmentNumber) =>
  getClaimsForShipment(shipmentNumber).length;

export const getLatestClaimForShipment = (shipmentNumber) => {
  const claims = getClaimsForShipment(shipmentNumber);
  if (!claims.length) return null;
  return claims[0];
};

export const getOpenClaimsCount = (shipmentNumber) =>
  getClaimsForShipment(shipmentNumber).filter((c) => !CLOSED_CLAIM_STATUSES.has(c.status)).length;

export const computeClaimKpis = (claims = MOCK_CLAIMS) => {
  const openClaims = claims.filter((c) => !CLOSED_CLAIM_STATUSES.has(c.status)).length;
  const pendingPayment = claims
    .filter((c) => !CLOSED_CLAIM_STATUSES.has(c.status))
    .reduce((sum, c) => sum + (Number(c.totalClaimAmount) || 0) - (Number(c.amountPaid) || 0), 0);
  const paidLast90 = claims
    .filter((c) => c.status === "Claim Closed – Paid in Full")
    .reduce((sum, c) => sum + (Number(c.amountPaid) || 0), 0);

  return {
    openClaims,
    pendingPayment,
    paidLast90,
    avgDaysToClose: 27,
  };
};

export const filterClaims = (claims, { statusFilter, typeFilter, searchQuery }) => {
  let result = [...claims];

  if (statusFilter && statusFilter !== "All Statuses") {
    result = result.filter((c) => c.status === statusFilter);
  }
  if (typeFilter && typeFilter !== "All Claim Types") {
    result = result.filter((c) => c.claimType === typeFilter);
  }
  if (searchQuery?.trim()) {
    const q = searchQuery.trim().toLowerCase();
    result = result.filter(
      (c) =>
        c.claimNumber?.toLowerCase().includes(q) ||
        c.shipmentNumber?.toLowerCase().includes(q) ||
        c.poNumber?.toLowerCase().includes(q)
    );
  }

  return result;
};
